import { CORE_DATA_DIMENSIONS, type CoreDataDimension } from '@omnea-digital/authz-model'
import { AssertNeverError, UnknownError } from '@omnea-digital/toolbox'
import { useQuery } from '@tanstack/react-query'
import { CoreDataHttpClient } from '@omnea-app/core-data/coreDataHttpClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import type { paths } from '@omnea-app/core-data/coreDataHttpClient/coreDataGeneratedTypes'

type SystemCoreDataResponse = paths['/system-core-data/{entityType}']['get']['responses'][200]['content']['application/json']

type UserPropertyValue = { id: string; name: string; dimension: CoreDataDimension }

const valuesOf = (dimension: CoreDataDimension, data: SystemCoreDataResponse): ReadonlyArray<{ id: string; name: string }> => {
  switch (dimension) {
    case 'department':
      if ('departments' in data) return data.departments
      break
    case 'subsidiary':
      if ('subsidiaries' in data) return data.subsidiaries
      break
    default:
      throw new AssertNeverError(dimension)
  }
  // An empty picker would read as an organisation with none.
  throw new UnknownError({ message: `Unexpected system core data response for ${dimension}` })
}

const UserPropertyValuesQueryKey = 'GetUserPropertyValues'

/** The live departments and subsidiaries a grant can name; the endpoint refuses an archived one. */
export const useUserPropertyValues = (options: { enabled: boolean }) => {
  const { getToken, user } = useAuthenticatedUser()
  return useQuery({
    queryKey: [UserPropertyValuesQueryKey, user.id],
    enabled: options.enabled,
    queryFn: async (): Promise<UserPropertyValue[]> => {
      const authorization = await getToken()
      const coreDataClient = new CoreDataHttpClient({ authorization })
      const valuesByDimension = await Promise.all(
        CORE_DATA_DIMENSIONS.map(async (dimension) => {
          const { data, error } = await coreDataClient.GET('/system-core-data/{entityType}', {
            params: { path: { entityType: dimension }, query: { includeArchived: false } },
          })
          throwIfApiError(error)
          return valuesOf(dimension, data).map((value) => ({ id: value.id, name: value.name, dimension }))
        })
      )
      return valuesByDimension.flat()
    },
  })
}
